import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import {
  descendantsOf,
  errorEnvelopeSchema,
  pathLabelOf,
  type BulkMoveContainerResult,
  type Container,
} from "@egalregal/shared";
import { PATH_SEPARATOR } from "@egalregal/shared";
import { ApiError, requests } from "../../shared/api";
import { log } from "../../logger";
import { useAppStore } from "../../shared/store/app";
import { useT } from "../../shared/i18n";
import { useShell } from "../shell/useShell";
import { DestinationEntryList, destinationEntryChoices } from "./DestinationEntryList";
import { DestinationParentPicker } from "./DestinationParentPicker";

/** What the dialog hands back once the batch has been committed or partly committed. */
export interface BulkMoveOutcome {
  result: BulkMoveContainerResult;
  entryId: string;
  /** Chosen parent; null means the destination entry root (always null for transfers). */
  parentId: string | null;
  destinationLabel: string;
}

interface BulkMoveDialogProps {
  open: boolean;
  /** The entry the selected rows were picked from; step one preselects it. */
  sourceEntryId: string;
  /** The container whose children are selected; never offered as a parent. */
  currentContainerId: string | null;
  selectedIds: readonly string[];
  onClose: () => void;
  onComplete: (outcome: BulkMoveOutcome) => void;
}

type Step = "entry" | "parent" | "confirm";

interface MoveError {
  message: string;
  rows: string[];
}

/**
 * Destination flow for a child-container batch (bulk-move 05, ADR-0040).
 *
 * Step one preselects the current entry and offers own plus writable shared
 * entries. A same-home destination continues to the parent picker; a
 * cross-home destination skips it and shows one aggregate transfer
 * confirmation. The current container, every selected container and their
 * descendants are excluded from the tree and the search (cycle safety).
 *
 * Pre-validation failures keep the dialog open with the server's reason and
 * row details. A committed batch is reported through {@link onComplete}; the
 * Browse view owns the reload, snackbar and selection retention.
 */
export function BulkMoveDialog({
  open,
  sourceEntryId,
  currentContainerId,
  selectedIds,
  onClose,
  onComplete,
}: BulkMoveDialogProps) {
  const t = useT();
  const shell = useShell();
  const entries = useAppStore((s) => s.entries);
  const sharedWithMe = useAppStore((s) => s.sharedWithMe);
  const containers = useAppStore((s) => s.containers);
  const bulkMove = useAppStore((s) => s.bulkMove);
  const [step, setStep] = useState<Step>("entry");
  const [selectedId, setSelectedId] = useState<string>(sourceEntryId);
  const [destContainers, setDestContainers] = useState<Container[]>([]);
  const [parentId, setParentId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<MoveError | null>(null);

  const choices = destinationEntryChoices(entries, sharedWithMe);
  const chosen = choices.find((choice) => choice.id === selectedId);
  const sourceOwner = sharedWithMe.find((share) => share.entry.id === sourceEntryId)?.owner;
  const crossesHome = chosen !== undefined && chosen.owner !== sourceOwner;
  const entryLabel = chosen?.label ?? "";

  useEffect(() => {
    if (!open) return;
    setStep("entry");
    setSelectedId(sourceEntryId);
    setDestContainers([]);
    setParentId(null);
    setBusy(false);
    setError(null);
  }, [open, sourceEntryId]);

  // The source container is the level being browsed; the rows are its direct
  // children (or the entry roots). None may become a parent of the batch, and
  // a selected subtree may not land inside itself.
  const excluded = new Set<string>();
  if (currentContainerId) excluded.add(currentContainerId);
  for (const id of selectedIds) {
    excluded.add(id);
    for (const descendant of descendantsOf(containers, id)) excluded.add(descendant.id);
  }

  /** The default destination (unchanged parent) would not move anything. */
  const noOp = selectedId === sourceEntryId && parentId === currentContainerId;

  const advance = async () => {
    setBusy(true);
    setError(null);
    try {
      if (crossesHome) {
        setDestContainers([]);
        setStep("confirm");
      } else {
        setDestContainers(await requests.containers.list(selectedId));
        setParentId(null);
        setStep("parent");
      }
    } catch (err) {
      log.warn({ err, entryId: selectedId.slice(0, 7) }, "[bulk-move] destination entry load failed");
      setError({ message: t("bulkDestinationLoadFailed"), rows: [] });
    } finally {
      setBusy(false);
    }
  };

  const commit = async (chosenParentId: string | null) => {
    const rows = selectedIds.flatMap((id) => {
      const container = containers.find((candidate) => candidate.id === id);
      return container ? [{ id, version: container.version }] : [];
    });
    if (rows.length !== selectedIds.length) {
      setError({ message: t("bulkMoveRowsChanged"), rows: [] });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const targetParentId = crossesHome ? null : chosenParentId;
      const result = crossesHome
        ? await bulkMove(rows, selectedId, null, true)
        : await bulkMove(rows, selectedId, targetParentId);
      const parent = targetParentId ? destContainers.find((c) => c.id === targetParentId) : undefined;
      onComplete({
        result,
        entryId: selectedId,
        parentId: targetParentId,
        destinationLabel: parent ? `${entryLabel}${PATH_SEPARATOR}${pathLabelOf(destContainers, parent.id)}` : entryLabel,
      });
    } catch (err) {
      setError(describeMoveError(err, containers, t("bulkMoveFailed")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={shell === "phone"}
      data-testid="bulk-move-dialog"
    >
      <DialogTitle>{t("bulkMoveTitle")}</DialogTitle>

      {step === "entry" ? (
        <>
          <DialogContent data-testid="bulk-move-entry-step">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t("bulkPickEntry")}
            </Typography>
            {error && <MoveErrorAlert error={error} />}
            <DestinationEntryList
              choices={choices}
              selectedId={selectedId}
              onSelect={(id) => {
                setError(null);
                setSelectedId(id);
              }}
              currentEntryId={sourceEntryId}
              sourceOwner={sourceOwner}
              testIdPrefix="bulk-entry"
            />
          </DialogContent>
          <DialogActions sx={DIALOG_ACTIONS_SX}>
            <Button onClick={onClose}>{t("cancel")}</Button>
            <Button
              variant="contained"
              disabled={!selectedId || busy}
              data-testid="bulk-entry-confirm"
              onClick={() => void advance()}
            >
              {t("next")}
            </Button>
          </DialogActions>
        </>
      ) : step === "parent" ? (
        <>
          <DialogContent data-testid="bulk-move-parent-step">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t("movePickParent", { entry: entryLabel })}
            </Typography>
            {error && <MoveErrorAlert error={error} />}
            {noOp && (
              <Alert severity="info" sx={{ mb: 1.5 }} data-testid="bulk-move-noop">
                {t("bulkMoveNoOp")}
              </Alert>
            )}
            <DestinationParentPicker
              containers={destContainers}
              entryLabel={entryLabel}
              value={parentId}
              onChange={setParentId}
              excludeIds={excluded}
              testIdPrefix="bulk-parent"
            />
          </DialogContent>
          <DialogActions sx={DIALOG_ACTIONS_SX}>
            <Button
              onClick={() => {
                setError(null);
                setStep("entry");
              }}
              disabled={busy}
              data-testid="bulk-parent-back"
            >
              {t("back")}
            </Button>
            <Button
              onClick={() => void commit(null)}
              disabled={busy || (selectedId === sourceEntryId && currentContainerId === null)}
              data-testid="bulk-move-to-root"
            >
              {t("moveToRoot")}
            </Button>
            <Button
              variant="contained"
              disabled={busy || parentId === null || noOp}
              data-testid="bulk-parent-confirm"
              onClick={() => void commit(parentId)}
            >
              {t("bulkMoveConfirm", { count: String(selectedIds.length) })}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent data-testid="bulk-move-confirm-step">
            <Typography variant="body1" sx={{ mb: 1 }} data-testid="bulk-transfer-summary">
              {t("bulkTransferBody", { count: String(selectedIds.length), entry: entryLabel })}
            </Typography>
            <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="bulk-transfer-warning">
              {t("bulkTransferWarning")}
            </Alert>
            {selectedIds.length > 1 && (
              <List dense data-testid="bulk-transfer-rows">
                {selectedIds.map((id) => {
                  const container = containers.find((candidate) => candidate.id === id);
                  return container ? (
                    <ListItem key={id}>
                      <ListItemText primary={container.name} />
                    </ListItem>
                  ) : null;
                })}
              </List>
            )}
            {error && <MoveErrorAlert error={error} />}
          </DialogContent>
          <DialogActions sx={DIALOG_ACTIONS_SX}>
            <Button
              onClick={() => {
                setError(null);
                setStep("entry");
              }}
              disabled={busy}
              data-testid="bulk-transfer-back"
            >
              {t("back")}
            </Button>
            <Button
              variant="contained"
              color="warning"
              disabled={busy}
              data-testid="bulk-transfer-confirm"
              onClick={() => void commit(null)}
            >
              {t("bulkTransferConfirm", { count: String(selectedIds.length) })}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

/**
 * Keep a pre-validation failure in the dialog and name the offending rows.
 * The server answers before writing anything (ADR-0040), so the selection can
 * simply be corrected and retried.
 */
function MoveErrorAlert({ error }: { error: MoveError }) {
  return (
    <Alert severity="error" sx={{ mb: 1.5 }} data-testid="bulk-move-error">
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {error.message}
      </Typography>
      {error.rows.length > 0 && (
        <List dense disablePadding>
          {error.rows.map((row, index) => (
            <ListItem key={`${row}-${index}`} disablePadding>
              <ListItemText primary={row} />
            </ListItem>
          ))}
        </List>
      )}
    </Alert>
  );
}

/** Keep the phone's full-screen dialog actions above the home indicator. */
const DIALOG_ACTIONS_SX = { pb: "calc(8px + env(safe-area-inset-bottom))" };

/** Turn an API error envelope into a dialog message plus row-keyed reasons. */
function describeMoveError(
  err: unknown,
  containers: readonly Container[],
  fallback: string,
): MoveError {
  if (err instanceof ApiError) {
    const parsed = errorEnvelopeSchema.safeParse(err.body);
    if (parsed.success) {
      const details = parsed.data.error.details ?? [];
      return {
        message: parsed.data.error.message,
        rows: details.map((detail) => {
          const id = detail.path[1];
          const name = containers.find((container) => container.id === id)?.name;
          return name ? `${name}: ${detail.message}` : detail.message;
        }),
      };
    }
  }
  return { message: err instanceof Error ? err.message : fallback, rows: [] };
}
